import { Component, computed, effect, inject, input, output, signal } from '@angular/core';
import { RouterLink } from '@angular/router';
import { Store } from '@ngrx/store';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';

import {
  DeployChannelType,
  DeployContentKind,
  DeployMessage,
  DeployPlanConfigItem
} from '@core/models/deploy-plan-config.model';
import { GbeErrorCodes } from '@core/models/gbe/gbe-error-codes.enum';
import { PlanDeployStatus } from '@core/models/plan.model';
import { hasAdminOrAbove } from '@core/models/user-role.constants';
import { DeployPlanConfigService } from '@core/services/deploy-plan-config/deploy-plan-config.service';
import { readContextRefs } from '@core/services/gbe-error-parser/warning-normalizer';
import { ICONS } from '@core/services/icons/icons.service';
import { NotificationService } from '@core/services/notification/notification.service';
import { PlansService } from '@core/services/plans/plans.service';
import { selectUser } from '@core/store/auth/auth.selectors';
import { getChannelMeta } from '@core/utils/deploy-channel.utils';
import { isNil } from '@core/utils/shared-utils';

import { ButtonComponent } from '@shared/components/button/button.component';
import { CheckboxComponent } from '@shared/components/checkbox/checkbox.component';

import { DeployConfirmModalComponent } from '../deploy-confirm-modal/deploy-confirm-modal.component';
import { DeployContentViewerComponent } from '../deploy-content-viewer/deploy-content-viewer.component';
import {
  DeployEntityPickerComponent,
  PickerCandidate
} from '../deploy-entity-picker/deploy-entity-picker.component';
import { DeployMessagesTableComponent } from '../deploy-messages-table/deploy-messages-table.component';
import { DeployMessagesStore } from '../deploy-messages.store';
import {
  buildDeployBody,
  failedWithoutSending,
  isSendLocked
} from '../deploy-selection.utils';
import { DeploySendHistoryModalComponent } from '../deploy-send-history-modal/deploy-send-history-modal.component';
import { isSiteBlocker, splitSiteWarnings } from '../deploy-site-warnings.utils';
import { DeploySitesTableComponent } from '../deploy-sites-table/deploy-sites-table.component';
import { DeployStatusDetailComponent } from '../deploy-status-detail/deploy-status-detail.component';
import { DeployViewSummaryComponent } from '../deploy-view-summary/deploy-view-summary.component';
import { ResolvedDeployView, resolveDeployView } from '../deploy-view.utils';
import { DeployWarningsListComponent } from '../deploy-warnings-list/deploy-warnings-list.component';

const MAX_LISTED_ROUTES = 5;

interface OpenContent {
  title: string;
  view: ResolvedDeployView;
}

@Component({
  selector: 'app-deploy-channel-panel',
  imports: [
    RouterLink,
    TranslatePipe,
    ButtonComponent,
    CheckboxComponent,
    DeployMessagesTableComponent,
    DeploySitesTableComponent,
    DeployStatusDetailComponent,
    DeployViewSummaryComponent,
    DeployWarningsListComponent,
    DeployContentViewerComponent,
    DeploySendHistoryModalComponent,
    DeployEntityPickerComponent,
    DeployConfirmModalComponent
  ],
  templateUrl: './deploy-channel-panel.component.html',
  styles: ``
})
export class DeployChannelPanelComponent {
  readonly config = input.required<DeployPlanConfigItem>();
  readonly planId = input.required<string | null>();
  readonly deployEnabled = input.required<boolean>();
  /**
   * When this configuration last deployed successfully, from the plan's deploy status. A
   * `view`-kind `/messages` response carries no send history of its own.
   */
  readonly lastSentAt = input<Date | string | null>(null);
  /** The configuration's latest deploy for this plan; null when it has never deployed. */
  readonly deployStatus = input<PlanDeployStatus | null>(null);

  readonly deployStarted = output<string>();

  protected readonly ICONS = ICONS;
  protected readonly DeployContentKind = DeployContentKind;
  protected readonly getChannelMeta = getChannelMeta;

  private readonly store = inject(DeployMessagesStore);
  private readonly ngrxStore = inject(Store);
  private readonly plans = inject(PlansService);
  private readonly configService = inject(DeployPlanConfigService);
  private readonly notification = inject(NotificationService);
  private readonly translate = inject(TranslateService);

  /**
   * Channel setup lives in the admin settings, which only an administrator may open — this
   * page is reachable by any user with planning rights, so an ungated link would dead-end
   * most of the people looking at it.
   */
  private readonly currentUser = this.ngrxStore.selectSignal(selectUser);
  protected readonly canReachChannelSettings = computed(() =>
    hasAdminOrAbove(this.currentUser()?.role)
  );

  protected readonly selectedOnly = signal(false);
  protected readonly isDeploying = signal(false);
  protected readonly openContent = signal<OpenContent | null>(null);
  protected readonly openHistory = signal<DeployMessage | null>(null);
  protected readonly pickerOpen = signal(false);
  protected readonly confirmOpen = signal(false);
  private readonly isSavingRestriction = signal(false);

  /** Uids of an accepted deploy still awaiting its status, kept in the store across switches. */
  protected readonly sendingUids = computed(() => this.store.deploying(this.config().id));
  protected readonly isSending = computed(() => this.store.isDeploying(this.config().id));

  protected readonly state = computed(() => this.store.state(this.config().id));

  /**
   * The stored selection minus anything that cannot be sent, so the count, the Deploy
   * label, the ticked rows and the request body all agree. The store is deliberately kept
   * across a refetch, and a message can come back invalid after its uid was ticked.
   */
  protected readonly selection = computed<ReadonlySet<string>>(() => {
    const stored = this.store.selection(this.config().id);
    const messages = this.messages();
    if (stored.size === 0 || messages.length === 0) return stored;

    const allowRedeploy = this.config().allowRedeploy;
    return new Set(
      messages
        .filter(
          (message) => stored.has(message.uid) && !isSendLocked(message, allowRedeploy)
        )
        .map((message) => message.uid)
    );
  });

  protected readonly messages = computed<DeployMessage[]>(() => {
    const response = this.state().response;
    return response?.contentKind === DeployContentKind.MESSAGES
      ? response.content.messages
      : [];
  });

  /**
   * The messages the deploy block names as needing a look: attempted and never got through.
   * Derived here rather than in the block, which has no listing of its own, and through the same
   * helper `deriveChannelStats` counts with, so the list and the badge cannot disagree.
   *
   * Deliberately not the rows badged `Failed` — that includes a message sent and then failed on
   * re-send, which is already out. See `failedWithoutSending`.
   */
  protected readonly failedUids = computed<string[] | null>(() => {
    const state = this.state();
    // `null` while the answer is still unknown, which is not the same as none — see the block's
    // input. Only a per-message channel has rows that can displace the channel's own error, and
    // only its listing can name them, so until that listing answers (either way: a failed fetch
    // settles the question too) nothing about the failures can be asserted. A payload channel's
    // set is empty by construction, so its error is never withheld for a listing it does not
    // have. `supportsDeployInSteps` is the per-message signal available before the fetch.
    if (this.config().supportsDeployInSteps && state.response === null && !state.error) {
      return null;
    }
    return this.messages()
      .filter((message) => failedWithoutSending(message))
      .map((message) => message.uid);
  });

  protected readonly viewContent = computed(() => {
    const response = this.state().response;
    return response?.contentKind === DeployContentKind.VIEW ? response.content : null;
  });

  /**
   * The entities a view-kind channel can export. `null` covers both "no restriction" and
   * "nothing pickable": neither has a table worth drawing, and the summary above already
   * reports validity.
   */
  protected readonly siteUids = computed<string[] | null>(() => {
    const uids = this.viewContent()?.validUids ?? null;
    return uids !== null && uids.length > 0 ? uids : null;
  });

  /**
   * Whether an unticked site table means "export every entity" rather than "export nothing".
   * GAEL's `warehouse_uids: []` is its no-filter case, which is why an empty restriction is
   * refused here and why confirming an empty picker clears the filter rather than saving it.
   */
  protected readonly sitesEmptyMeansAll = computed(
    () => this.config().channel === DeployChannelType.GAEL
  );

  /**
   * A site table with nothing ticked has no safe deploy: the restriction on the
   * configuration is `[]`, so the export covers every warehouse rather than the operator's
   * choice, and there is no undeploy. The operator picks a row first.
   */
  protected readonly siteSelectionMissing = computed(
    () =>
      this.siteUids() !== null && this.sitesEmptyMeansAll() && this.selection().size === 0
  );

  /**
   * Whether the response's `view` still describes the restriction the operator sees ticked.
   * It does not from the moment a row is ticked until the refetch lands — the save covers
   * two requests, and only the second sets `refreshing` — so anything that would show that
   * body is offering the pre-change export.
   */
  protected readonly viewStale = computed(
    () => this.isSavingRestriction() || this.state().refreshing
  );

  /** A view's warnings, with the ones the site table shows on a site's row taken out. */
  private readonly viewWarnings = computed(() =>
    splitSiteWarnings(this.viewContent()?.warnings ?? [])
  );

  protected readonly siteWarnings = computed(() => this.viewWarnings().bySite);

  protected readonly viewSummaryContent = computed(() => {
    const content = this.viewContent();
    return content === null ? null : { ...content, warnings: this.viewWarnings().rest };
  });

  /**
   * Routes that make a single-send channel refuse to send anything. Only a `view` response is
   * read: a stepped channel locks the affected messages row by row instead. A blocked site
   * only counts while it is ticked — the export leaves out an unticked one anyway.
   */
  protected readonly unvalidatedRouteUids = computed<string[]>(() => {
    const content = this.viewContent();
    if (content === null || content.isValid) return [];
    const siteWarnings = [...this.siteWarnings()]
      .filter(([siteUid]) => this.isTicked(siteUid))
      .flatMap(([, warnings]) => warnings.filter(isSiteBlocker));
    const uids = [...siteWarnings, ...this.viewWarnings().rest.filter(isSiteBlocker)]
      .flatMap((warning) => readContextRefs(warning.details) ?? [])
      .flatMap((ref) => (ref.uid === null ? [] : [ref.uid]));
    return [...new Set(uids)];
  });

  /**
   * The file is refused for missing data (`fail_on_missing_required_fields`) and every problem
   * sits on a site's row, so nothing above the table would otherwise say why.
   */
  protected readonly siteProblemsBlockFile = computed(() => {
    const content = this.viewContent();
    return (
      content !== null &&
      !content.isValid &&
      this.unvalidatedRouteUids().length === 0 &&
      this.viewWarnings().rest.length === 0 &&
      [...this.siteWarnings().keys()].some((siteUid) => this.isTicked(siteUid))
    );
  });

  private isTicked(siteUid: string): boolean {
    const selection = this.selection();
    return selection.has(siteUid) || (selection.size === 0 && this.sitesEmptyMeansAll());
  }

  /**
   * The banner names a handful of routes and counts the rest: a plan can hold hundreds of
   * unvalidated routes, and each one is listed on its warehouse's row or in the summary below.
   */
  protected readonly unvalidatedSummary = computed(() => {
    const uids = this.unvalidatedRouteUids();
    if (uids.length === 0) return null;
    return {
      total: uids.length,
      shown: uids.slice(0, MAX_LISTED_ROUTES),
      hidden: Math.max(0, uids.length - MAX_LISTED_ROUTES)
    };
  });

  /**
   * Configuration-level warnings: what belongs to no single message. A view has no messages,
   * so its warnings are the content's own, listed by `app-deploy-view-summary` and, for those
   * about one site, on that site's row.
   */
  protected readonly configWarnings = computed(() => {
    const response = this.state().response;
    return response?.contentKind === DeployContentKind.MESSAGES
      ? response.content.warnings
      : [];
  });

  /**
   * `valid_uids: null` means no restriction, so no picker is offered at all; `[]` means
   * nothing is pickable, which is a real state with its own copy inside the modal.
   */
  protected readonly pickerCandidates = computed<PickerCandidate[] | null>(() => {
    const response = this.state().response;
    if (response === null) return null;
    if (response.contentKind === DeployContentKind.MESSAGES) {
      const allowRedeploy = this.config().allowRedeploy;
      return response.content.messages
        .filter((message) => !isSendLocked(message, allowRedeploy))
        .map((message) => ({ uid: message.uid, label: message.label }));
    }
    const validUids = response.content.validUids;
    return validUids === null ? null : validUids.map((uid) => ({ uid, label: null }));
  });

  private readonly deployBody = computed(() =>
    buildDeployBody(
      this.selection(),
      this.messages().map((message) => message.uid),
      this.config().supportsDeployInSteps
    )
  );

  /**
   * `allow_redeploy: false` on a channel that sends one indivisible payload.
   *
   * The flag is otherwise applied only per message row (`isSendLocked`), and a payload channel
   * has no rows — so nothing consulted it at all and a GAEL export could be sent again and
   * again. Its unit is the whole (plan, configuration), which is exactly what its send history
   * is keyed by: `DeployPlanChannelSend` holds one upserted row per pair.
   *
   * `lastSentAt` is the only signal available here. A `view`-kind `/messages` response carries
   * no send history, and GAEL implements no `listSendHistory`, so its `/sends` is empty — the
   * summary's live value is the one thing that says a payload already went out.
   */
  protected readonly resendBlocked = computed(
    () =>
      !this.config().supportsDeployInSteps &&
      !this.config().allowRedeploy &&
      !isNil(this.lastSentAt())
  );

  /**
   * The sites the last export covered, or `null` when no such claim can be made.
   *
   * This is an inference, not a record: GAEL writes one upserted `DeployPlanChannelSend` per
   * (plan, configuration) and implements no `listSendHistory`, so nothing anywhere says which
   * *sites* a payload carried. What is known is that an export succeeded (`lastSentAt`) and
   * which sites the restriction covers — and an empty restriction is GAEL's "every warehouse",
   * so it colours the whole table rather than none of it.
   *
   * The two only coincide while the restriction cannot have moved since that export, which is
   * why this is gated on `resendBlocked`: it is the same condition that renders the table
   * read-only, so what is shown is what went out. On a channel that still accepts a re-send the
   * operator can tick a site after deploying, and green would then claim a send that never
   * happened — the one direction this must not be wrong in, on an export bound for a customer.
   */
  protected readonly deployedSiteUids = computed<ReadonlySet<string> | null>(() => {
    const uids = this.siteUids();
    if (uids === null || !this.resendBlocked()) return null;
    const restriction = this.selection();
    if (restriction.size > 0) return restriction;
    return this.sitesEmptyMeansAll() ? new Set(uids) : null;
  });

  protected readonly canDeploy = computed(() => {
    if (!this.config().isEnabled) return false;
    if (!this.deployEnabled() || this.isDeploying() || isNil(this.planId())) return false;
    if (this.isSending()) return false;
    if (this.siteSelectionMissing()) return false;
    // A view awaiting its refetch still judges the previous selection, so a site unticked a
    // moment ago would lift the block before the back has re-checked the file.
    if (this.viewStale()) return false;
    if (this.resendBlocked()) return false;
    if (this.unvalidatedRouteUids().length > 0) return false;
    return this.deployBody() !== null;
  });

  /**
   * How many messages the request carries, or null for a channel that deploys as one body —
   * which is also the question the confirmation asks.
   */
  protected readonly confirmCount = computed<number | null>(() => {
    if (!this.config().supportsDeployInSteps) return null;
    const body = this.deployBody();
    if (body === null) return null;
    return 'uids' in body ? body.uids.length : this.messages().length;
  });

  /**
   * Selected messages that already went out successfully, so the confirmation can name what
   * is about to be sent a second time. Empty unless re-sending is allowed, because otherwise
   * those rows are locked out of the selection in the first place.
   */
  protected readonly resendUids = computed<string[]>(() => {
    const selected = this.selection();
    if (selected.size === 0) return [];
    return this.messages()
      .filter(
        (message) =>
          selected.has(message.uid) && message.sends.some((attempt) => attempt.success)
      )
      .map((message) => message.uid);
  });

  constructor() {
    effect(() => {
      const planId = this.planId();
      if (isNil(planId)) return;
      this.store.load(this.config(), planId);
    });

    // The filter belongs to the configuration on screen, not to the panel's lifetime.
    effect(() => {
      this.config();
      this.selectedOnly.set(false);
    });
  }

  /**
   * None of the three selection paths below touches `selectedOnly`: the filter is the
   * operator's own control, and switching it on for them hides every row not yet picked, so
   * the next one cannot be ticked until they find the filter and turn it back off.
   */
  protected onToggleUid(uid: string): void {
    this.store.toggle(this.config().id, uid);
  }

  /** The header box replacing the whole selection. */
  protected onSelectionRequested(uids: string[]): void {
    this.store.setSelection(this.config().id, uids);
  }

  protected onPickerConfirmed(uids: string[]): void {
    this.pickerOpen.set(false);
    if (this.config().channel === DeployChannelType.GAEL) {
      this.saveWarehouseUids(uids);
      return;
    }
    this.store.setSelection(this.config().id, uids);
  }

  /**
   * A view-kind channel keeps its restriction on the configuration rather than in the deploy
   * body, so ticking a row saves it instead of only staging it.
   */
  protected onSiteToggle(uid: string): void {
    const next = new Set(this.selection());
    if (next.has(uid)) next.delete(uid);
    else next.add(uid);
    this.saveWarehouseUids([...next]);
  }

  protected onSiteSelectionRequested(uids: string[]): void {
    this.saveWarehouseUids(uids);
  }

  protected onViewMessage(message: DeployMessage): void {
    const view = this.resolveView(message.view);
    if (view === null) return;
    this.openContent.set({ title: message.label, view });
  }

  protected onViewSingle(): void {
    const view = this.resolveView(this.viewContent()?.view);
    if (view === null) return;
    this.openContent.set({ title: this.config().name, view });
  }

  private resolveView(view: unknown): ResolvedDeployView | null {
    return resolveDeployView(view, this.state().response?.contentType ?? 'text/plain');
  }

  protected copyContent(content: string): void {
    navigator.clipboard.writeText(content).then(
      () =>
        this.notification.success(
          this.translate.instant('table.cell.overlay_actions.copy.message_success'),
          '',
          2000
        ),
      () =>
        this.notification.error(
          this.translate.instant('table.cell.overlay_actions.copy.message_error'),
          ''
        )
    );
  }

  private saveWarehouseUids(uids: string[]): void {
    const config = this.config();
    // The backend returns `warehouse_uids` on no endpoint, so the ticked rows are only ever
    // what this session recorded. Applied up front for that reason, and put back on failure
    // rather than left showing a restriction that was never stored.
    const previous = this.store.selection(config.id);
    this.store.setSelection(config.id, uids);
    this.isSavingRestriction.set(true);
    this.configService
      .patch$(config.id, config.channel, { channelConfig: { warehouse_uids: uids } })
      .subscribe({
        next: () => {
          // Refreshed rather than invalidated: the restriction changes what the exported
          // body contains, but the rows and the pane itself must stay put.
          const planId = this.planId();
          if (!isNil(planId)) this.store.refresh(config, planId);
          // Cleared after `refresh`, which sets `refreshing` in the same tick, so the two
          // halves of the window join rather than leaving a frame where neither is set.
          this.isSavingRestriction.set(false);
        },
        error: () => {
          this.isSavingRestriction.set(false);
          this.store.setSelection(config.id, previous);
          this.notification.error(
            this.translate.instant('features.plan.deploy.detail.save_error'),
            ''
          );
        }
      });
  }

  /**
   * Every deploy is confirmed, not just a re-send: an e-mail cannot be recalled, a CSV cannot
   * be un-uploaded, and a GedMouv tour cannot be deleted from here at all.
   */
  protected requestDeploy(): void {
    if (!this.canDeploy()) return;
    this.confirmOpen.set(true);
  }

  protected onConfirmCancelled(): void {
    this.confirmOpen.set(false);
  }

  protected onConfirmed(): void {
    this.confirmOpen.set(false);
    this.deploy();
  }

  private deploy(): void {
    const planId = this.planId();
    const body = this.deployBody();
    if (!this.canDeploy() || isNil(planId) || body === null) return;

    const uids = 'uids' in body ? body.uids : undefined;
    this.isDeploying.set(true);
    this.plans.deployConfig$(planId, this.config().id, uids).subscribe({
      next: () => {
        this.isDeploying.set(false);
        // Marks the picked rows in flight so the table changes on the 202 rather than only
        // when the channel reports back. Cleared by the page on the terminal status.
        this.store.markDeploying(this.config().id, uids ?? []);
        this.notification.success(
          this.translate.instant('features.plan.deploy.deploy_success'),
          '',
          4000
        );
        this.deployStarted.emit(this.config().id);
      },
      // The interceptor hands subscribers the parsed envelope flat, not nested under `error`.
      error: (error: { errorCode?: string; details?: { uids?: string[] } }) => {
        this.isDeploying.set(false);
        if (error?.errorCode === 'messages_already_deployed') {
          this.notification.error(
            this.translate.instant('features.plan.deploy.already_sent_error', {
              uids: (error.details?.uids ?? []).join(', ')
            }),
            ''
          );
          return;
        }
        // A switched-off channel already reported itself through the interceptor's coded
        // message; repeating the generic failure here would contradict it.
        if (error?.errorCode === GbeErrorCodes.DEPLOY_CHANNEL_DISABLED) return;
        this.notification.error(
          this.translate.instant('features.plan.deploy.deploy_error'),
          ''
        );
      }
    });
  }
}
